import { FunctionService } from 'src/app/services/general/function.service';
import { MenuController, Platform } from '@ionic/angular';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { PopupService } from 'src/app/services/general/popup.service';
import { AppService } from 'src/app/services/general/app.service';
import { Subscription } from 'rxjs';
import { LinkService } from 'src/app/services/general/link.service';
import { DeviceService } from 'src/app/services/device/device.service';
import { StorageService } from 'src/app/services/general/storage.service';

@Component({
  selector: 'app-app-update',
  templateUrl: './app-update.page.html',
  styleUrls: ['./app-update.page.scss'],
})
export class AppUpdatePage implements OnInit, OnDestroy {

  /**
   * Current version
   */
  currentVersion: string;
  /**
   * New version
   */
  newVersion: string;
  minVersion: string;

  checkNewVersion: boolean;

  timestamp: string;
  btnCount: number;
  storageCount: number;

  webPlatform: string;

  forceUpdate: boolean;

  browserType: string;

  newVersionText: string;

  private newVersionSubscription: Subscription;
  private minVersionSubscription: Subscription;

  private backHelpPage: boolean;

  /**
   * Constructor
   * @param route route
   * @param router router
   * @param platform platform
   * @param modalController modal controller
   * @param appService app service
   * @param popupService popup service
   */
  constructor(
    private platform: Platform,
    private route: ActivatedRoute,
    private router: Router,
    private menuCtrl: MenuController,
    private appService: AppService,
    private deviceService: DeviceService,
    private linkService: LinkService,
    private popupService: PopupService,
    private storageService: StorageService,
    private functionService: FunctionService,
  ) { }

  /**
   * Ng on init
   */
  ngOnInit() {
    this.watchRoute();
  }

  ngOnDestroy(): void {
    this.unwatchNewVersion();
    this.unwatchMinVersion();
  }

  ionViewWillEnter() {
    this.initialize();
  }

  ionViewWillLeave() {
    this.unwatchNewVersion();
    this.unwatchMinVersion();
  }

  ionViewDidLeave() {
    if (this.forceUpdate) {
      this.appService.watchUrl();
    }
  }

  async initialize() {
    await this.getCurrentVersion();
    await this.watchNewVersion();
    await this.watchMinVersion();
    await this.checkStorageCount();
    this.forceUpdate = this.appService.forceUpdate;
    this.webPlatform = this.getWebPlatform();

    this.checkNewVersion = this.getCheckNewVersion();
    this.browserType = this.getBrowserType();
    this.newVersionText = this.getNewVersion();
  }
  /**
   * Watch Route
   */
  watchRoute() {
    this.route.params.subscribe((param: Params) => {
      const route = this.router.getCurrentNavigation();
      const state = route?.extras?.state;

      if (state?.newVersion) {
        this.newVersion = state.newVersion.toString();
      } else if (param?.newVersion) {
        this.newVersion = param.newVersion.toString();
      }

      if (state?.minVersion) {
        this.minVersion = state.minVersion.toString();
      } else if (param?.minVersion) {
        this.minVersion = param.minVersion.toString();
      }

      if (state?.backHelpPage) {
        this.backHelpPage = true;
      } else if (param?.backHelpPage) {
        this.backHelpPage = true;
      } else {
        this.backHelpPage = false;
      }

      if (!this.newVersion && this.appService?.newVersion) {
        this.newVersion = this.appService.newVersion;
      }
      if (!this.minVersion && this.appService?.minVersion) {
        this.minVersion = this.appService.minVersion;
      }

      this.checkNewVersion = this.getCheckNewVersion();
      this.browserType = this.getBrowserType();
      this.newVersionText = this.getNewVersion();
    });



    this.route.queryParamMap.subscribe(params => {
      const timestamp = params.get('timestamp');
      this.timestamp = timestamp ? timestamp : '';

      const btnCount = params.get('btnCount');
      this.btnCount = btnCount ? Number(btnCount) : 0;

    });
  }

  async watchNewVersion() {
    if (!this.newVersionSubscription) {
      this.newVersionSubscription = this.appService.observableNewVersion.subscribe(async (newVersion: string) => {
        if (newVersion) {
          this.newVersion = newVersion;
          await this.getCurrentVersion();
        }
      });
    }
    
  }

  async unwatchNewVersion() {
    if (this.newVersionSubscription) {
      this.newVersionSubscription.unsubscribe();
      this.newVersionSubscription = null;
    }
  }

  async watchMinVersion() {
    if (!this.minVersionSubscription) {
      this.minVersionSubscription = this.appService.observableMinVersion.subscribe(async (minVersion: string) => {
        if (minVersion) {
          this.minVersion = minVersion;
          await this.getCurrentVersion();
        }
      });
    }
    
  }

  async unwatchMinVersion() {
    if (this.minVersionSubscription) {
      this.minVersionSubscription.unsubscribe();
      this.minVersionSubscription = null;
    }
  }

  async checkStorageCount() {
    if (this.currentVersion && this.getCheckNewVersion() && this.btnCount) {
      const newVersion = this.getNewVersion();
      if (newVersion) {
        const storageCount = await this.storageService.get('APP_UPDATE_' + newVersion);
        if (storageCount) {
          this.storageCount = Number(storageCount);
        } else {
          this.storageCount = 0;
        }
        if (!this.storageCount) {
          this.storageCount = 0;
        }
        this.storageCount += 1;
  
        await this.storageService.set('APP_UPDATE_' + newVersion, this.storageCount);
      }
    }
  }

  isWeb() {
    return !this.platform.is('hybrid');
  }

  getWebPlatform() {
    if (this.isWeb()) {
      if (this.platform.is('mobileweb') || this.platform.is('mobile')) {
        return 'mobile';
      } else {
        const deviceInfo = this.deviceService.deviceInfo;
        if (deviceInfo?.operatingSystem === 'mac') {
          return 'mac';
        } else if (deviceInfo?.operatingSystem === 'windows' || deviceInfo?.operatingSystem === 'unknown') {
          return 'windows';
        }
      }
    }
    return 'unknown';
  }

  async getCurrentVersion() {
    this.currentVersion = await this.appService.getAppVersion();
  }

  getCheckNewVersion(): boolean {
    if (this.forceUpdate) {
      if (this.functionService.compareVersion(this.minVersion, this.currentVersion, '>')) {
        this.menuCtrl.enable(false);
        return true;
      } else {
        return false;
      }
    } else {
      return this.functionService.compareVersion(this.newVersion, this.currentVersion, '>');
    }
  }

  getNewVersion(): string {
    let version = this.currentVersion;
    if (this.minVersion && this.forceUpdate) {
      version = this.minVersion;
    } else if (this.newVersion) {
      version = this.newVersion;
    }
    if (this.functionService.compareVersion(this.currentVersion, version, '>')) {
      return this.currentVersion;
    } else {
      return version;
    }
  }

  getBrowserType() {
    const browserType = this.deviceService.getDeviceBrowser();
    if (browserType === 'chrome' || browserType === 'safari') {
      return browserType;
    } else {
      return 'chrome';
    }
  }

  goBack() {
    if (this.backHelpPage) {
      this.goHelpPage();
    } else {
      this.goMainPage();
    }
  }

  goHelpPage() {
    this.router.navigate(['/about/help']);
  }

  /**
   * Go update
   */
  async goUpdate() {
    await this.popupService.presentLoading();
    if (this.platform.is('hybrid')) {
      this.appService.goStore();
    } else {
      // Get the current URL
      const currentUrl = window.location.href;

      // Create a new timestamp
      const timestamp = new Date().getTime();
      this.btnCount += 1;

      // Check if the 'timestamp' parameter already exists in the URL
      if (currentUrl.includes('timestamp=')) {
        // If it exists, replace its value with the new timestamp
        const updatedUrl = currentUrl.replace(/(\?|&)timestamp=\d+/, `$1timestamp=${timestamp}`).replace(/(\?|&)btnCount=\d+/, `$1btnCount=${this.btnCount}`);
        window.location.href = updatedUrl;
      } else {
        // If it doesn't exist, add the 'timestamp' parameter to the URL
        const separator = currentUrl.includes('?') ? '&' : '?';
        const updatedUrl = currentUrl + separator + 'timestamp=' + timestamp + '&btnCount=' + this.btnCount;
        window.location.href = updatedUrl;
      }
      
      await this.functionService.delay(200);
      window.location.reload();
    }
    this.popupService.dismissLoading();
  }

  /**
   * Go main page
   */
  goMainPage() {
    const newVersion = this.getNewVersion();
    this.storageService.set('APP_UPDATE_' + newVersion, 0);
    this.linkService.goMainPage();
  }

}
